import React from "react";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

/** Simple brand circular determinate-style loader for React.Suspense fallback. */
export default function RouteFallback({
  label = "Loading…",
  i18nKey = "",
  progress = null,
}) {
  const size = 40;
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const auth = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const resolvedLabel = i18nKey ? t(i18nKey, label) : label;

  const rawProgress = Number.isFinite(progress)
    ? progress
    : auth?.loading
      ? auth?.loadingProgress
      : 85;

  const resolvedProgress = Math.max(0, Math.min(100, Number(rawProgress) || 0));
  const dashoffset = circumference - (resolvedProgress / 100) * circumference;

  if (auth.profileError && !auth.profile) {
    const fr = String(languageCode || "").startsWith("fr");
    return <div className="w-full h-[100dvh] grid place-items-center">
      <div className="space-y-4 text-center" role="alert">
        <p>{fr ? "Impossible de charger votre profil pour le moment." : "Your profile could not be loaded right now."}</p>
        <button type="button" className="text-primary" onClick={() => auth.user?.id ? auth.refreshProfile() : window.location.reload()}>
          {fr ? "Réessayer" : "Try again"}
        </button>
      </div>
    </div>;
  }
  return (
    <div className="w-full h-[100dvh] grid place-items-center">
      <div className="inline-flex items-center gap-3">
        <svg
          width={size}
          height={size}
          viewBox="0 0 48 48"
          aria-label={resolvedLabel || "Loading"}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(resolvedProgress)}
        >
          {/* track */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="rgb(var(--afrimed-primary))"
            strokeOpacity="0.16"
            strokeWidth="4"
            fill="none"
          />
          {/* determinate-style progress ring */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="rgb(var(--afrimed-primary))"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
            transform="rotate(-90 24 24)"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={dashoffset}
            style={{ transition: "stroke-dashoffset 220ms ease" }}
          />
        </svg>
        <span className="text-body-medium text-on-surface-var">{resolvedLabel}</span>
      </div>
    </div>
  );
}
